import type { QueryClient } from '@tanstack/react-query'

/**
 * Everything that counts vehicles.
 *
 * A mutation that changes the fleet has to move all of it. Adding a vehicle used to
 * invalidate only `['vehicles']`, which is correct for the table but leaves the
 * severity strip above it and the dashboard showing the old counts — so the new
 * vehicle appeared in the list while the numbers over it disagreed, which reads as
 * the add having half-failed.
 *
 * `['compliance-summary']` and `['dashboard']` were read by two screens and
 * invalidated by nothing at all; they self-healed after their staleTime, which is
 * why it looked intermittent.
 *
 * Prefix matching does the work: `['compliance-summary']` matches every
 * `['compliance-summary', siteId, typeId]` already cached, so filter permutations
 * need no enumeration here.
 *
 * Entity-scoped keys — `['vehicle', id]`, `['activity', id]`, `['documents', id]` —
 * are deliberately NOT included. They need the id, and a caller that changed one
 * vehicle should say which. This helper is only the fleet-wide half.
 */
export const invalidateFleet = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['vehicles'] }),
    queryClient.invalidateQueries({ queryKey: ['vehicle-lookup'] }),
    queryClient.invalidateQueries({ queryKey: ['compliance-summary'] }),
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  ])

/**
 * Everything the bell and the reminder screens read.
 *
 * Saving a rule changes the rules list, the vehicle's own tab, and — once the
 * poller next runs — the feed and its count. Clearing a notification changes the
 * feed and the count but no rule.
 *
 * One helper for both, because the alternative is each call site remembering
 * which of four keys its particular mutation happens to touch, and the last time
 * that judgement was left to call sites three keys ended up invalidated by
 * nothing at all.
 */
export const invalidateReminders = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['reminders'] }),
    queryClient.invalidateQueries({ queryKey: ['reminders-vehicle'] }),
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    queryClient.invalidateQueries({ queryKey: ['notification-count'] }),
  ])

/**
 * Everything the machinery screens read.
 *
 * A sibling of invalidateFleet rather than a line inside it: machinery and the
 * vehicle register share no data, so an import of 562 machines has no business
 * refetching the dashboard, and adding a vehicle has none refetching the
 * machinery grid.
 *
 * Prefix matching covers ['machinery', query] without enumerating filter
 * permutations. ['machine', id] is deliberately excluded — it needs the id, and
 * a caller that changed one machine should say which.
 */
export const invalidateMachinery = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['machinery'] }),
    queryClient.invalidateQueries({ queryKey: ['machinery-facets'] }),
  ])
